import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";

import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { getIdentityInterstitialUrl } from "@/components/signup/utils";
import {
    trackContinueWithSocialSignups,
    trackDataFoundationsAccountCreationComponent,
    trackFormSubmit,
    trackInvalidEmail,
    trackSignFormLoad,
    trackSubmitButtonClick,
    trackTimeToFormReady,
    getSocialButtonUrl,
    ufoEventSignupFormLoad,
    getNameYourSiteRedirectUrl,
    getContinueParam,
    getLocalePath
} from "@/components/signup/utils";
import TryIntent from "@/components/try-intent/src";
import { type Product, useUserContext } from "@/components/user-context";
import { useLocation } from "@/utils/location-provider";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client";
export interface AccountCreationContext {
    dataFoundationsActionSubjectId: "embedded" | "standalone";
    edition?: string;
    formError: boolean;
    hasSubmitted: boolean;
    identityCobranding: string;
    isReadyForInvalid: boolean;
    isSubmitting: boolean;
    onClickSocialButton: (provider: string) => void;
    onClickSubmitButton: () => void;
    onSubmit: ({ email }: { email: string }) => Promise<void>;
    product?: Product;
    redirectTo: string;
    setIsEmailValid: Dispatch<SetStateAction<boolean>>;
    setIsReadyForInvalid: Dispatch<SetStateAction<boolean>>;
    setTypeAheadEmail: Dispatch<SetStateAction<string>>;
    typeAheadEmail: string;
    tryIntentAnalyticsEventHasFired: boolean;
}

export const AccountCreationContext =
    createContext<AccountCreationContext | null>(null);

export const useAccountCreationContext = (): AccountCreationContext => {
    const context = useContext(AccountCreationContext);
    if (!context) {
        throw new Error(
            "useAccountCreationContext must be used within a AccountCreationContextProvider"
        );
    }
    return context;
};

export const AccountCreationContextProvider = ({
    continueUrl,
    identityCobranding = "wac",
    templateId,
    children
}: PropsWithChildren<{
    continueUrl?: string;
    identityCobranding?: string;
    templateId?: string;
    // eslint-disable-next-line complexity
}>) => {
    const location = useLocation();
    const isEmbedded = !location.pathname.endsWith("/try/cloud/signup");
    const { product, edition } = useUserContext();
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isReadyForInvalid, setIsReadyForInvalid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(false);
    const [typeAheadEmail, setTypeAheadEmail] = useState("");
    const [
        tryIntentAnalyticsEventHasFired,
        setTryIntentAnalyticsEventHasFired
    ] = useState(false);
    const localePath = getLocalePath(intl.locale);

    const continueParam = getContinueParam(location);

    const dataFoundationsActionSubjectId = !!isEmbedded
        ? "embedded"
        : "standalone";

    // default experiment params
    if (product && identityCobranding === "wac") {
        if (product.name === "confluence") {
            identityCobranding = "wac--otp-confluence";
        } else if (product.name === "jira") {
            identityCobranding = "wac--otp-jira";
        } else if (product.name === "loom") {
            identityCobranding = "wac--loom";
        } else if (product.name === "jira-product-discovery") {
            identityCobranding = "wac--jpd";
        } else if (product.name === "jira-servicedesk") {
            identityCobranding = "wac--jsm";
        } else if (product.name === "service-collection") {
            identityCobranding = "wac--service-collection";
        } else if (product.name === "teamwork-collection") {
            identityCobranding = "wac--teamwork-collection";
        }
    }

    const redirectTo = continueUrl
        ? continueUrl
        : continueParam
        ? continueParam
        : getNameYourSiteRedirectUrl({
              bundle: product?.bundle,
              edition,
              localePath,
              templateId,
              application: identityCobranding
          });

    useEffect(() => {
        if (!analyticsClient) return;

        trackDataFoundationsAccountCreationComponent(createAnalyticsEvent, {
            actionSubjectId: dataFoundationsActionSubjectId,
            productKey: product?.key,
            edition
        });

        ufoEventSignupFormLoad.success({
            customData: {
                webPlatform: "bxp-ui",
                reactComponent: "DefaultSignupForm",
                isLoggedIn: false
            }
        });

        trackSignFormLoad(createAnalyticsEvent);
        trackTimeToFormReady(createAnalyticsEvent, performance.now());
    }, [
        createAnalyticsEvent,
        edition,
        product?.key,
        dataFoundationsActionSubjectId,
        analyticsClient
    ]);

    const onSubmit = useCallback(
        async ({ email }: { readonly email: string }) => {
            setIsReadyForInvalid(true);
            if (!email || !isEmailValid) {
                trackInvalidEmail(createAnalyticsEvent);
                return;
            }

            trackFormSubmit(createAnalyticsEvent, product?.key, edition);
            setIsSubmitting(true);

            try {
                // check identity interstitial redirect experiments for enrollment
                const url = getIdentityInterstitialUrl(
                    email,
                    redirectTo,
                    identityCobranding
                );
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "succeeded",
                    actionSubject: "accountCreationInterstitialRedirect",
                    containerId: "bxp-ui-signup",
                    attributes: {
                        productKey: product?.key,
                        edition
                    }
                }).fire();
                window.location.assign(url);
                return;
            } catch (error) {
                setHasSubmitted(true);
                setFormError(true);
                setIsSubmitting(false);

                createAnalyticsEvent({
                    eventType: "ui",
                    action: "errored",
                    actionSubject: "accountCreationInterstitialRedirect",
                    containerId: "bxp-ui-signup",
                    attributes: {
                        productKey: product?.key,
                        edition
                    }
                }).fire();
            }
        },
        [
            createAnalyticsEvent,
            edition,
            identityCobranding,
            isEmailValid,
            product,
            redirectTo
        ]
    );

    const onClickSocialButton = (provider: string) => {
        trackContinueWithSocialSignups(
            createAnalyticsEvent,
            provider,
            product?.key,
            edition
        );
        const socialRedirectUrl = getSocialButtonUrl({
            redirectTo,
            identityCobranding,
            provider
        });
        window.location.assign(socialRedirectUrl);
    };

    const onClickSubmitButton = () =>
        trackSubmitButtonClick(createAnalyticsEvent);

    return (
        <AccountCreationContext.Provider
            value={{
                dataFoundationsActionSubjectId,
                edition,
                formError,
                hasSubmitted,
                identityCobranding,
                isReadyForInvalid,
                isSubmitting,
                onClickSocialButton,
                onClickSubmitButton,
                onSubmit,
                product,
                redirectTo,
                setIsEmailValid,
                setIsReadyForInvalid,
                setTypeAheadEmail,
                typeAheadEmail,
                tryIntentAnalyticsEventHasFired
            }}
        >
            <TryIntent
                edition={edition}
                intentType="form"
                productKey={product?.key}
                onAnalyticsEventFired={() =>
                    !tryIntentAnalyticsEventHasFired &&
                    setTryIntentAnalyticsEventHasFired(true)
                }
            >
                {children}
            </TryIntent>
        </AccountCreationContext.Provider>
    );
};
